import React, { useEffect, useState } from 'react';
import {
  Box, Stack, TextField, Typography, Chip, Paper, Divider,
  ToggleButtonGroup, ToggleButton,
  Select, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, StatusChip, GlassIconButton } from 'dashboard/components/common';
import AdminTableFooter from './AdminTableFooter';

const PAGE_SIZE = 100;

const targetLabel = (t) => {
  if (t === 'webapp') return 'Storefront';
  if (t === 'dashboard') return 'Dashboard';
  if (t === 'page') return 'Page';
  return 'Coming soon';
};

// Compound sort key (`<field>:<dir>`) is split into the two server
// params right before the request goes out. Keeping it as one piece
// of state makes the `<Select>` trivial.
const SORT_OPTIONS = [
  ['createdAt:desc', 'Newest'],
  ['createdAt:asc',  'Oldest'],
  ['domain:asc',     'Domain A→Z'],
  ['domain:desc',    'Domain Z→A'],
  ['status:asc',     'Status'],
];

export default function DomainsTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('createdAt:desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [sort_by, sort_dir] = sort.split(':');

  const onExport = () => {
    setExporting(true);
    API.adminExportDomains({
      search,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort_by,
      sort_dir,
    })
      .catch((err) => toast.error(err?.message || 'Failed to export domains'))
      .finally(() => setExporting(false));
  };

  useEffect(() => { setPage(1); }, [search, statusFilter, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      API.adminListDomains({
        search,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort_by,
        sort_dir,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res?.items || []);
          setTotal(res?.total || 0);
        })
        .catch((err) => toast.error(err?.message || 'Failed to load domains', { toastId: 'admin-domains' }))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, statusFilter, sort_by, sort_dir, page]);

  return (
    <Box>
      <Stack direction='row' alignItems='center' mb='12px' spacing='12px' flexWrap='wrap'>
        <TextField
          size='small'
          placeholder='Search by domain…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <ToggleButtonGroup
          size='small'
          exclusive
          value={statusFilter}
          onChange={(_e, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton value='verified'>Verified</ToggleButton>
          <ToggleButton value='pending'>Pending</ToggleButton>
          <ToggleButton value='failed'>Failed</ToggleButton>
        </ToggleButtonGroup>
        <Select
          size='small'
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          {SORT_OPTIONS.map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </Select>
        <Box flex={1} />
        <GlassIconButton
          size='small'
          label='Download CSV of the current view'
          disabled={exporting}
          disabledReason={exporting ? 'Export already running — the file will download when it is ready.' : ''}
          onClick={onExport}
        >
          <DownloadIcon fontSize='small' />
        </GlassIconButton>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No domains match your search.</EmptyState>
        ) : (
          <Stack spacing='10px'>
            {rows.map((d) => (
              <Paper
                key={d._id}
                elevation={0}
                sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', p: '14px' }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap='10px'>
                  <Box>
                    <Stack direction='row' spacing='8px' alignItems='center'>
                      <Typography fontWeight='700'>{d.domain}</Typography>
                      <StatusChip status={d.status} />
                    </Stack>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Brand: {d.brand?.name || '—'} · Owner: {d.owner?.email || '—'}
                    </Typography>
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''}
                  </Typography>
                </Stack>
                {d.subdomains?.length > 0 && (
                  <>
                    <Divider sx={{ my: '10px' }} />
                    <Typography variant='caption' color='text.secondary' fontWeight='600' textTransform='uppercase' display='block' mb='6px'>
                      Subdomains ({d.subdomains.length})
                    </Typography>
                    <Stack direction='row' spacing='8px' flexWrap='wrap' useFlexGap>
                      {d.subdomains.map((s) => (
                        <Chip
                          key={s._id}
                          size='small'
                          variant={s.enabled ? 'filled' : 'outlined'}
                          label={`${s.subdomain || '(apex)'} → ${targetLabel(s.target_type)}`}
                          color={s.target_type === 'coming_soon' ? 'default' : 'primary'}
                        />
                      ))}
                    </Stack>
                  </>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </LoadingOverlay>
      <AdminTableFooter
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={setPage}
        label='domains'
      />
    </Box>
  );
}
